/**
 * attributionApi — web wrapper around `@wg/api/attribution`. Mirrors the RN
 * surface (`checkAttribution(clickId, referralCode, source, sourceDetail)`,
 * `claimAttribution(_, installId)`) so AttributionContext can be a 1:1 port.
 *
 * Web has no Install Referrer or expo-device, so the fingerprint is derived
 * from `navigator` + `screen` + `Intl.DateTimeFormat`. Auth/unauth dispatch
 * matches RN: pre-auth checks use `unauthClient`, post-auth claims use the
 * authenticated `httpClient`.
 */
import {
  createAttributionApi,
  isValidReferralCode,
  normalizeReferralCode,
  type AttributionApi,
} from '@wg/api/attribution';
import type { AttributionSource, DeviceFingerprint } from '@wg/types';
import { httpClient, unauthClient } from '../http';
import { getSessionInfo } from '../auth';
import logger from '../utils/logger';

export type {
  AttributionMatchRequest,
  AttributionMatchResponse,
  AttributionSource,
  ClaimAttributionResponse,
  DeviceFingerprint,
} from '@wg/types';

const authedApi: AttributionApi = createAttributionApi(httpClient, logger);
const unauthedApi: AttributionApi = createAttributionApi(unauthClient, logger);

const FINGERPRINT_STORAGE_KEY = 'attribution.device_id';

function getStableDeviceId(): string {
  if (typeof window === 'undefined') return 'web-unknown';
  try {
    const existing = window.localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(FINGERPRINT_STORAGE_KEY, id);
    return id;
  } catch {
    return 'web-unknown';
  }
}

export async function collectDeviceFingerprint(): Promise<DeviceFingerprint> {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const lang =
    (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

  let timezone = 'UTC';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {}

  const width = typeof screen !== 'undefined' ? screen.width : 0;
  const height = typeof screen !== 'undefined' ? screen.height : 0;

  return {
    device_id: getStableDeviceId(),
    platform: 'web',
    os_version: typeof navigator !== 'undefined' ? navigator.platform : 'web',
    device_model: 'browser',
    screen_width: width,
    screen_height: height,
    timezone,
    locale: lang,
    user_agent: ua,
  };
}

export async function checkAttribution(
  clickId?: string,
  referralCode?: string,
  source?: AttributionSource,
  sourceDetail?: string,
) {
  const fingerprint = await collectDeviceFingerprint();
  const sessionInfo = await getSessionInfo();
  const api = sessionInfo.token ? authedApi : unauthedApi;
  return api.checkAttribution({
    fingerprint,
    clickId,
    referralCode,
    source,
    sourceDetail,
  });
}

export async function claimAttribution(
  _authClient: unknown,
  installId?: string,
) {
  const fingerprint = await collectDeviceFingerprint();
  return authedApi.claimAttribution(fingerprint, installId);
}

/** Web has no Install Referrer; always returns null. */
export async function getInstallReferrer(): Promise<string | null> {
  return null;
}

/** Back-compat alias for callers that imported the simplified web fingerprint. */
export const getWebDeviceFingerprint = collectDeviceFingerprint;

export { isValidReferralCode, normalizeReferralCode };

export const attributionApi = {
  collectDeviceFingerprint,
  checkAttribution,
  claimAttribution,
  getInstallReferrer,
};

export default attributionApi;
